import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Select, { SingleValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import HockeyFieldBackground from '../components/HockeyFieldBackground';
import { LiveBadge } from '../components/live/LiveBadge';
import { PublicPageHeader } from '../components/public/PublicPageHeader';
import { PublicPageFooter } from '../components/public/PublicPageFooter';
import { Match, Lineup } from '../types/api';
import { apiService } from '../services/api';
import { parseMarkup, renderMarkupTokens } from '../utils/markupParser';
import { getZoneLongName } from '../constants/zones';
import { useTeam } from '../contexts/TeamContext';
import { useAuth } from '../contexts/AuthContext';
import { useDateFormat } from '../hooks/useDateFormat';
import { useAppResume } from '../hooks/useAppResume';
import { useVisibleAgain } from '../hooks/useVisibleAgain';
import { publicPlayerName } from '../utils/playerName';
import { badgeLiveMatch, publicLiveRoutePath } from '../utils/publicLive';

interface MatchSelectOption {
  value: number;
  label: string;
  match: Match;
}

// ResultSection Component
interface ResultSectionProps {
  match: Match;
}

const ResultSection: React.FC<ResultSectionProps> = ({ match }) => {
  const { t } = useTranslation(['playerView', 'common']);

  // Only show results if both scores are available and match is in the past
  const hasResult = match.ourResult !== null && match.theirResult !== null;
  const hasShootoutResult = match.shootoutOurResult !== null && match.shootoutTheirResult !== null;
  const matchDate = match.startTime ? new Date(match.startTime) : null;
  const isMatchInPast = matchDate && matchDate < new Date();
  
  if (!hasResult || !isMatchInPast) return null;

  const ourScore = match.ourResult!;
  const theirScore = match.theirResult!;
  const isWin = ourScore > theirScore;
  const isDraw = ourScore === theirScore;
  const isHome = match.homeOrAway === 'home';
  
  // For home games: Our score - Their score
  // For away games: Their score - Our score
  const firstScore = isHome ? ourScore : theirScore;
  const secondScore = isHome ? theirScore : ourScore;
  
  return (
    <div className="bg-white border rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-bold text-hockey-brown">{t('result.title')}</h2>
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
          isWin ? 'bg-green-100 text-green-800' :
          isDraw ? 'bg-yellow-100 text-yellow-800' :
          'bg-red-100 text-red-800'
        }`}>
          {isWin
            ? t('common:outcomes.won')
            : isDraw
              ? t('common:outcomes.draw')
              : t('common:outcomes.lost')}
        </span>
      </div>
      
      <div className="flex items-center justify-center space-x-6">
        <div className="text-3xl font-bold text-hockey-brown">
          {firstScore}
        </div>
        
        <div className="text-2xl font-bold text-gray-400">-</div>
        
        <div className="text-3xl font-bold text-hockey-brown">
          {secondScore}
        </div>
      </div>
      
      {hasShootoutResult && (
        <div className="mt-3 text-center">
          <div className="text-sm text-gray-600 mb-1">{t('result.afterShootout')}</div>
          <div className="text-lg font-medium text-hockey-brown">
            {isHome ? match.shootoutOurResult : match.shootoutTheirResult} - {isHome ? match.shootoutTheirResult : match.shootoutOurResult}
          </div>
        </div>
      )}
    </div>
  );
};

// PlayerListTable Component
interface PlayerListTableProps {
  match: Match;
  selectedLineup: Lineup | null;
}

const PlayerListTable: React.FC<PlayerListTableProps> = ({ match, selectedLineup }) => {
  const { t } = useTranslation(['playerView', 'common']);

  if (!selectedLineup) return null;

  // Get all players (field + substitutes) sorted by position type
  const fieldPlayers = selectedLineup.positions.filter(pos => pos.formationPosition !== null && pos.player);
  const substitutes = selectedLineup.positions
    .filter(pos => pos.formationPosition === null && pos.player)
    .sort((a, b) => a.id - b.id);

  // Group field players by zone
  const playersByZone = fieldPlayers.reduce((groups, position) => {
    const zone = position.formationPosition?.zone || 'unknown';
    if (!groups[zone]) groups[zone] = [];
    groups[zone].push(position);
    return groups;
  }, {} as Record<string, typeof fieldPlayers>);

  // Volgorde van achter naar voor; de namen komen uit common:zones.
  const ZONE_ORDER = ['goalkeeper', 'backs', 'midfield', 'front'];

  return (
    <div className="bg-white border rounded-lg p-4">
      <h2 className="font-bold text-hockey-brown mb-3">{t('lineup.title')}</h2>
      
      <div className="space-y-2">
        {/* Field Players Grouped by Zone */}
        {ZONE_ORDER.map(zone => {
          const zonePositions = playersByZone[zone];
          if (!zonePositions || zonePositions.length === 0) return null;
          
          return (
            <div key={zone} className="space-y-1">
              <h3 className="text-sm font-semibold text-gray-600 border-b border-gray-200 pb-1">
                {getZoneLongName(t, zone)}
              </h3>
              {zonePositions
                .sort((a, b) => (a.formationPosition?.sortOrder || 0) - (b.formationPosition?.sortOrder || 0))
                .map((position) => (
                  <div key={position.id} className="flex items-center space-x-3 py-1.5 border-b border-gray-100 last:border-b-0">
                    <div 
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                      style={{ 
                        backgroundColor: '#2A1C0D', // Always club brown for field players
                        border: '2px solid rgba(0,0,0,0.1)'
                      }}
                    >
                      <span className="text-white font-bold text-sm">
                        {position.player?.shirtNumber}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {position.player ? publicPlayerName(position.player) : ''}
                        {match.captain?.id === position.player?.id && ` (${t('common:captain')})`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {position.formationPosition?.positionName}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          );
        })}
        
        {/* Substitutes */}
        {substitutes.length > 0 && (
          <>
            <div className="pt-2 pb-1">
              <h3 className="text-sm font-semibold text-gray-600 border-b border-gray-200 pb-1">
                {t('common:lineup.substitutes')}
              </h3>
            </div>
            {substitutes.map((position) => (
              <div key={position.id} className="flex items-center space-x-3 py-1.5 border-b border-gray-100 last:border-b-0">
                <div 
                  className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ 
                    backgroundColor: position.color || '#2A1C0D',
                    border: position.color ? '2px solid rgba(0,0,0,0.1)' : undefined
                  }}
                >
                  <span className="text-white font-bold text-sm">
                    {position.player?.shirtNumber}
                  </span>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {position.player ? publicPlayerName(position.player) : ''}
                    {match.captain?.id === position.player?.id && ` (${t('common:captain')})`}
                  </div>
                  <div className="text-xs text-gray-500">
                    {t('common:lineup.substitute')}
                  </div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

interface PlayerViewProps {
  matchId?: number;
}

const PlayerView: React.FC<PlayerViewProps> = ({ matchId }) => {
  const { t } = useTranslation(['playerView', 'common']);
  const { formatDate } = useDateFormat();
  const { currentTeam, loading: teamLoading } = useTeam();
  const { isAuthenticated, user } = useAuth();
  const [activeMatch, setActiveMatch] = useState<Match | null>(null);
  const [selectedLineup, setSelectedLineup] = useState<Lineup | null>(null);
  const [loading, setLoading] = useState(true);
  // Sleutel in plaats van vertaalde tekst, zodat een zichtbare fout meegaat in een taalwissel.
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [previousMatches, setPreviousMatches] = useState<Match[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<Match | null>(null);
  // De laatst opgehaalde wedstrijden, voor de live-status van de badge in de kop. Ververst ook
  // los van de rest, zodat de badge klopt zonder de gekozen wedstrijd terug te zetten.
  const [publicMatches, setPublicMatches] = useState<Match[]>([]);

  // Bij het terugkeren in de app opnieuw ophalen: de opstelling kan intussen gewijzigd
  // zijn, en het ververst meteen de offline-cache.
  const [reloadToken, setReloadToken] = useState(0);
  useAppResume(() => setReloadToken((token) => token + 1));

  // Een wedstrijd kan live gaan terwijl deze pagina in een tabblad openstaat. Dan alleen de
  // live-status bijwerken: alles opnieuw laden zou een gekozen oude wedstrijd terugzetten.
  useVisibleAgain(() => {
    if (!currentTeam?.publicId) return;
    apiService
      .getPublicMatches(currentTeam.publicId, currentTeam.slug)
      .then(setPublicMatches)
      .catch(() => undefined);
  });

  // Alleen de eerste keer een laadscherm. Bij het verversen na een resume blijft de
  // huidige opstelling staan in plaats van weg te flitsen.
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const loadMatchData = async () => {
      // Don't load if teams are still loading
      if (teamLoading) return;

      // If no team is available after loading, show error
      if (!currentTeam) {
        setErrorKey('errors.noTeam');
        setLoading(false);
        return;
      }

      try {
        if (!hasLoadedRef.current) setLoading(true);
        setErrorKey(null);

        if (!currentTeam.publicId) {
          setErrorKey('errors.expiredLink');
          setLoading(false);
          return;
        }

        const matches = await apiService.getPublicMatches(
          currentTeam.publicId,
          currentTeam.slug
        );
        const currentMatch = matches.find(match => match.isCurrent);
        setActiveMatch(currentMatch || null);
        setPublicMatches(matches);

        // Get matches on or before the active match date (if active match exists, otherwise show past matches)
        // Also include matches on the same day as the active match, even if their time is later
        const activeMatchDate = currentMatch?.startTime ? new Date(currentMatch.startTime) : new Date();

        // Helper function to check if two dates are on the same day
        const isSameDay = (date1: Date, date2: Date): boolean => {
          return date1.getFullYear() === date2.getFullYear() &&
                 date1.getMonth() === date2.getMonth() &&
                 date1.getDate() === date2.getDate();
        };

        const pastMatches = matches
          .filter(match => {
            if (match.isCurrent || !match.startTime) return false;
            const matchDate = new Date(match.startTime);
            // Include matches that are either:
            // 1. Before the active match date, OR
            // 2. On the same day as the active match (regardless of time)
            return matchDate <= activeMatchDate || isSameDay(matchDate, activeMatchDate);
          })
          .sort((a, b) => new Date(b.startTime!).getTime() - new Date(a.startTime!).getTime())
          .slice(0, 10); // Limit to last 10 matches
        setPreviousMatches(pastMatches);

        // If matchId is provided in props, load that specific match
        if (matchId) {
          const specificMatch = matches.find(m => m.matchId === matchId);
          if (specificMatch) {
            setSelectedMatch(specificMatch);
            // Also load the lineup for the specific match
            try {
              const lineups = await apiService.getPublicLineups(
                currentTeam.publicId,
                currentTeam.slug,
                matchId
              );
              const defaultLineup = lineups[0] || null;
              setSelectedLineup(defaultLineup);
            } catch (err) {
              console.error('Error loading match lineups:', err);
              setSelectedLineup(null);
            }
          } else {
            // Match ID not found in this team's matches
            setErrorKey('errors.matchNotFound');
          }
        } else {
          // Set selected match to current active match initially
          setSelectedMatch(currentMatch || null);
        }

      } catch (err) {
        setErrorKey('errors.loadFailed');
        console.error('Error loading match data:', err);
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
      }
    };

    loadMatchData();
  }, [currentTeam, teamLoading, matchId, reloadToken]);

  /** Label in de wedstrijdkiezer; stond eerder twee keer uitgeschreven in de JSX. */
  const matchOptionLabel = (match: Match): string => {
    const date = formatDate(match.startTime, 'abbreviated');

    if (match.matchId === activeMatch?.matchId) {
      return t('matchPicker.current', { opponent: match.opponent, date });
    }

    if (match.ourResult !== null && match.theirResult !== null) {
      return t('matchPicker.pastWithScore', {
        opponent: match.opponent,
        date,
        ourScore: match.ourResult,
        theirScore: match.theirResult,
      });
    }

    return t('matchPicker.past', { opponent: match.opponent, date });
  };

  const handleMatchSelectChange = async (selectedOption: SingleValue<MatchSelectOption>) => {
    if (!selectedOption) {
      setSelectedMatch(activeMatch);
      setSelectedLineup(null);
      return;
    }
    
    try {
      setSelectedMatch(selectedOption.match);

      if (!currentTeam?.publicId) {
        setSelectedLineup(null);
        return;
      }

      // Load lineups for the selected match
      const lineups = await apiService.getPublicLineups(
        currentTeam.publicId,
        currentTeam.slug,
        selectedOption.match.matchId
      );
      const defaultLineup = lineups[0] || null;
      setSelectedLineup(defaultLineup);

    } catch (err) {
      console.error('Error loading match lineups:', err);
      setSelectedLineup(null);
    }
  };


  if (loading || teamLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hockey-blue mx-auto mb-4"></div>
          <p className="text-gray-600">
            {teamLoading ? t('loading.team') : t('loading.match')}
          </p>
        </div>
      </div>
    );
  }

  if (errorKey) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center text-red-600">
          <p>{t(errorKey)}</p>
        </div>
      </div>
    );
  }

  // Rood zolang er een verslag loopt; na afloop grijs bij de getoonde wedstrijd. De badge leidt
  // naar het verslag van precies die wedstrijd.
  const badgeMatch = badgeLiveMatch(publicMatches, selectedMatch?.matchId ?? null);

  return (
    <div className="min-h-screen bg-white">
      <PublicPageHeader
        team={currentTeam}
        match={selectedMatch}
        badge={
          badgeMatch && currentTeam?.publicId ? (
            <Link
              to={publicLiveRoutePath(currentTeam.publicId, currentTeam.slug, badgeMatch.matchId)}
              aria-label={t(badgeMatch.liveStatus === 'running' ? 'live:fans.follow' : 'live:fans.review')}
              className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
            >
              <LiveBadge match={badgeMatch} />
            </Link>
          ) : null
        }
      />
      
      {/* Main Content Area */}
      <div className="p-4">
        {/* Responsive 2-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {/* Left Column: Hockey Field (2/3 width on large screens) */}
          <div className="lg:col-span-2">
            <HockeyFieldBackground
              match={selectedMatch}
              selectedLineup={selectedLineup}
              onLineupChange={setSelectedLineup}
              publicId={currentTeam?.publicId}
              teamSlug={currentTeam?.slug}
            />
          </div>
          
          {/* Right Column: Results and Player List (1/3 width on large screens) */}
          <div className="lg:col-span-1">
            {selectedMatch && <ResultSection match={selectedMatch} />}
            {selectedMatch && <PlayerListTable match={selectedMatch} selectedLineup={selectedLineup} />}
          </div>
        </div>
        
        {/* Team Agreements - Full width below the 2-column layout */}
        {selectedMatch && (selectedMatch.teamAgreements || selectedMatch.field) && (
          <div className="max-w-6xl mx-auto mt-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h2 className="font-bold text-hockey-brown mb-2">{t('agreements.title')}</h2>
              {selectedMatch.teamAgreements && (
                <div className="text-sm text-hockey-brown mb-2 whitespace-pre-wrap">
                  {renderMarkupTokens(parseMarkup(selectedMatch.teamAgreements))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Footer */}
      <PublicPageFooter variant="opstelling">
        {/* Previous Matches Section */}
        {(previousMatches.length > 0 || activeMatch) && (
          <div className="mb-8">
            <h3 className="text-base font-medium mb-4">{t('matchPicker.title')}</h3>
            <div className="max-w-md">
              <Select
                value={selectedMatch ? {
                  value: selectedMatch.matchId,
                  label: matchOptionLabel(selectedMatch),
                  match: selectedMatch
                } : null}
                onChange={handleMatchSelectChange}
                options={(() => {
                  // Combine active match and previous matches
                  const allMatches = [
                    ...(activeMatch ? [activeMatch] : []),
                    ...previousMatches
                  ];

                  // Sort by date/time descending (most recent first)
                  const sortedMatches = allMatches.sort((a, b) => {
                    const dateA = a.startTime ? new Date(a.startTime).getTime() : 0;
                    const dateB = b.startTime ? new Date(b.startTime).getTime() : 0;
                    return dateB - dateA;
                  });

                  // Map to select options
                  return sortedMatches.map(match => ({
                    value: match.matchId,
                    label: matchOptionLabel(match),
                    match
                  }));
                })()}
                placeholder={t('matchPicker.placeholder')}
                isClearable={false}
                menuPlacement="top"
                styles={{
                  control: (provided) => ({
                    ...provided,
                    backgroundColor: '#374151',
                    borderColor: '#4B5563',
                    color: 'white',
                    fontSize: '16px',
                    '&:hover': {
                      borderColor: '#6B7280',
                    },
                  }),
                  menu: (provided) => ({
                    ...provided,
                    backgroundColor: '#374151',
                    fontSize: '16px',
                  }),
                  option: (provided, state) => ({
                    ...provided,
                    backgroundColor: state.isSelected ? '#8BD0F4' : state.isFocused ? '#4B5563' : '#374151',
                    color: state.isSelected ? 'black' : 'white',
                    fontSize: '16px',
                    '&:hover': {
                      backgroundColor: '#4B5563',
                    },
                  }),
                  singleValue: (provided) => ({
                    ...provided,
                    color: 'white',
                    fontSize: '16px',
                  }),
                  placeholder: (provided) => ({
                    ...provided,
                    color: '#9CA3AF',
                    fontSize: '16px',
                  }),
                  input: (provided) => ({
                    ...provided,
                    color: 'white',
                    fontSize: '16px',
                  }),
                }}
              />
            </div>
          </div>
        )}
      </PublicPageFooter>
      
      {/* Coach Navigation Button - Only visible for authenticated coaches */}
      {isAuthenticated && user && (
        <div className="fixed bottom-6 right-6 z-50">
          <a
            href="/coach"
            className="inline-flex items-center px-6 py-3 bg-hockey-blue text-white rounded-full shadow-lg hover:bg-hockey-blue-hover transition-all duration-200 text-sm font-medium"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('dashboardButton')}
          </a>
        </div>
      )}
    </div>
  );
};

export default PlayerView;